import { API } from "aws-amplify";

export type VerifyInviteResponse = {
  email: string;
  maskedEmail: string;
  redeemToken: string;
  expiresInSeconds: number;
  success?: boolean;
};

export type InviteApiErrorDetails = {
  maskedEmail?: string;
  [key: string]: unknown;
};

export class InviteApiError extends Error {
  status: number;
  details?: InviteApiErrorDetails;

  constructor(message: string, status: number, details?: InviteApiErrorDetails) {
    super(message);
    this.status = status;
    this.details = details;
    this.name = "InviteApiError";
  }
}

type InviteRequestType = "verify" | "completeInvite" | "completeOnboarding";

const defaultErrors: Record<InviteRequestType, string> = {
  verify: "Unable to verify invitation link.",
  completeInvite: "Unable to complete invitation.",
  completeOnboarding: "Unable to record EULA acceptance."
};

type AmplifyApiError = {
  response?: {
    status?: number;
    statusCode?: number;
    data?: InviteApiErrorDetails & {
      error?: string;
      message?: string;
    };
  };
};

const buildInviteApiError = (
  error: unknown,
  fallback: string
): InviteApiError => {
  const apiError = error as AmplifyApiError;
  const status =
    apiError?.response?.status ??
    apiError?.response?.statusCode ??
    500;

  const payload = apiError?.response?.data;
  const messageFromPayload =
    (typeof payload?.error === "string" && payload.error) ||
    (typeof payload?.message === "string" && payload.message);

  return new InviteApiError(
    messageFromPayload || fallback,
    status,
    payload
  );
};

async function postInviteRequest<TResponse>(
  path: string,
  body: Record<string, unknown>,
  type: InviteRequestType
): Promise<TResponse> {
  try {
    const response = await API.post("simpl-api", path, { body });
    return response as TResponse;
  } catch (error: unknown) {
    throw buildInviteApiError(error, defaultErrors[type]);
  }
}

export const verifyInviteRequest = (
  token: string
): Promise<VerifyInviteResponse> =>
  postInviteRequest("invites/verify", { token }, "verify");

export const completeInviteRequest = (input: {
  redeemToken: string;
  password: string;
}): Promise<{ maskedEmail?: string }> =>
  postInviteRequest("invites/complete", input, "completeInvite");

export const completeOnboardingRequest = async (): Promise<{
  eulaAcceptedDate: string;
  autoAssignedAssessments: number;
}> => {
  // Use API.post directly without body since this endpoint doesn't need any body data
  // Authentication is handled via the Authorization header from custom_header
  try {
    const response = await API.post("simpl-api", "complete-onboarding", {});
    return response as {
      eulaAcceptedDate: string;
      autoAssignedAssessments: number;
    };
  } catch (error: unknown) {
    throw buildInviteApiError(error, defaultErrors.completeOnboarding);
  }
};
